import type {
  HostMessage,
  SdkMessage,
  TakeScreenshotResponseMessage,
} from "@textclub/common/bridge";
import { resolveBrowserContinuationPath } from "@textclub/common/browser-continuation";
import {
  MAX_NOTIFICATION_SCHEDULE_DAYS,
  MIN_NOTIFICATION_SCHEDULE_MS,
} from "@textclub/common/constants";
import { estimatePublisherRevenue } from "@textclub/common/purchase-revenue";
import { createConversationId } from "@textclub/common/random-id";
import {
  doesBrowserSupportSMSLinks,
  smsLink,
} from "@textclub/common/sms-links";
import type {
  GamePlayer,
  PlayerDataUpdate,
  PlayerProfile,
} from "@textclub/common/types";
import { assertUnreachable } from "@textclub/common/utils";

import type { IFrameChildBridge } from "../bridge";
import {
  initializeMockAnalyticsEventsForMockBridge,
  recordMockAnalyticsEventForMockBridge,
} from "./analytics-events";
import {
  showLegalPageDialog,
  showLoginPrompt,
  showRedirectDialog,
} from "./dialogs";
import { MockRegistrationOverlay } from "./registration-overlay";

const DEFAULT_MOCK_PRODUCT_PRICE = 100;
const MOCK_PLAYER_STORAGE_KEY = "txtclub_mock_player";
const MOCK_NOTIFICATIONS_KEY = "txtclub_mock_notifications";

function generatePlayerId(): string {
  return "mock-" + Math.random().toString(36).substring(2, 15);
}

interface MockNotification {
  body: string;
  ctaText: string;
  assetReference?: string;
  /** The data URL itself stays out of localStorage, whose quota a few images would exhaust. */
  hasInlineAsset?: true;
  scheduledAt: string;
  scheduledInDays?: number;
  identifier?: string;
  createdAt: string;
}

function parseEntryPayload(): Record<string, unknown> {
  console.log(window.location.search);
  const entryPayload = new URLSearchParams(window.location.search).get(
    "entryPayload",
  );
  if (typeof entryPayload === "string") {
    try {
      return JSON.parse(entryPayload) as Record<string, unknown>;
    } catch (e) {
      alert("Entry payload set but not valid JSON. Please check the URL.");
      console.error("Invalid entry payload JSON:", e);
      return {};
    }
  }
  return {};
}

function storePlayer(player: GamePlayer) {
  localStorage.setItem(
    MOCK_PLAYER_STORAGE_KEY,
    JSON.stringify({
      ...player,
      entryPayload: undefined,
    }),
  );
}

function getStoredPlayer(): GamePlayer | null {
  const storedPlayer = localStorage.getItem(MOCK_PLAYER_STORAGE_KEY);
  if (!storedPlayer) {
    return null;
  }
  return JSON.parse(storedPlayer) as GamePlayer;
}

function loadGamePlayer(): { player: GamePlayer; profile: PlayerProfile } {
  // Check if we have the mock_login parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const mockLogin = urlParams.get("mock_login") === "true";

  const storedPlayer = getStoredPlayer();
  if (!storedPlayer) {
    return {
      profile: {
        username: "",
        avatarUrl: null,
      },
      player: {
        playerId: generatePlayerId(),
        registered: mockLogin, // Set registered to true if mock_login param exists
        username: null,
        avatarUrl: null,
        playerData: {},
        entryPayload: parseEntryPayload(),
      },
    };
  }

  const profile = {
    username: storedPlayer.username ?? "",
    avatarUrl: storedPlayer.avatarUrl ?? null,
  };

  const player = {
    ...storedPlayer,
    ...profile,
  };

  player.entryPayload = parseEntryPayload();

  // Override registration status if mock_login parameter is present
  if (mockLogin) {
    player.registered = true;
    // Store the updated player to persist registration
    storePlayer(player);

    // Remove the mock_login parameter from URL to prevent reregistration on page refresh
    if (window.history && window.history.replaceState) {
      urlParams.delete("mock_login");
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? `?${urlParams.toString()}` : "") +
        window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }

  return { player, profile };
}

export class MockBridge implements Pick<
  IFrameChildBridge,
  "onMessage" | "sendMessage"
> {
  public onMessage: (message: SdkMessage) => void = () => {};
  private messageLog: HostMessage[] = [];
  private mockPlayer: GamePlayer;
  private mockProfile: PlayerProfile;
  private onNotificationChange?: () => void;
  private onStateChange?: () => void;
  private pendingScreenshots = new Map<
    string,
    (message: TakeScreenshotResponseMessage) => void
  >();
  private registrationOverlay = new MockRegistrationOverlay({
    sendMessage: (message) => this.onMessage(message),
    onRegister: () => this.setMockPlayer({ registered: true }),
  });

  // Toggles.
  private rejectLoginLeaseAcquisition: boolean = false;
  private skipLoginSms: boolean = false;
  private mockIncompletePurchase: boolean = false;
  private mockPurchaseResponse:
    | "success"
    | "cancel"
    | "error:internal_error"
    | "error:invalid_product" = "success";
  private mockCompletePurchaseResponse:
    | "success"
    | "error:internal_error"
    | "error:invalid_token" = "success";
  private mockProducts: Array<{
    sku: string;
    name: string;
    description: string;
    price: number;
    currency: string;
  }> = [
    {
      sku: "gems_100",
      name: "100 Gems",
      description: "Get 100 gems to use in the game",
      price: 99,
      currency: "USD",
    },
    {
      sku: "gems_500",
      name: "500 Gems",
      description: "Get 500 gems with bonus value",
      price: 499,
      currency: "USD",
    },
    {
      sku: "premium_pass",
      name: "Premium Pass",
      description: "Unlock exclusive content and features",
      price: 999,
      currency: "USD",
    },
  ];
  private mockSubscriptions: Array<{
    sku: string;
    displayName: string;
    displayDescription: string | null;
    price: number;
    currency: string;
    billingPeriod: "monthly";
    status: "active" | "inactive";
    trialEligible: boolean;
    introOffer: { price: number; durationPeriods: number } | null;
    retentionOffer: { price: number; durationPeriods: number } | null;
    estimatedRevenue: number;
  }> = [
    {
      sku: "premium",
      displayName: "Premium Subscription",
      displayDescription: "Unlock premium features and exclusive content.",
      price: 9.99,
      currency: "USD",
      billingPeriod: "monthly",
      status: "inactive",
      trialEligible: true,
      introOffer: null,
      retentionOffer: null,
      // Trial-eligible, so the first period bills nothing.
      estimatedRevenue: 0,
    },
    {
      sku: "gold",
      displayName: "Gold Subscription",
      displayDescription: "An active membership with a retention discount.",
      price: 9.99,
      currency: "USD",
      billingPeriod: "monthly",
      status: "active",
      trialEligible: false,
      introOffer: null,
      retentionOffer: { price: 4.99, durationPeriods: 3 },
      estimatedRevenue: 8.42,
    },
    {
      sku: "intro",
      displayName: "Intro Subscription",
      displayDescription: "Half price for the first three months.",
      price: 9.99,
      currency: "USD",
      billingPeriod: "monthly",
      status: "inactive",
      trialEligible: false,
      introOffer: { price: 4.99, durationPeriods: 3 },
      retentionOffer: null,
      // The intro price applies to the first period, not the 9.99 list price.
      estimatedRevenue: 4.07,
    },
  ];
  private mockSubscriptionsSigned: string = "";
  private claimedRetentionSkus = new Set<string>();

  constructor() {
    initializeMockAnalyticsEventsForMockBridge();

    // Load stored mock player or use default (guest)
    const { player, profile } = loadGamePlayer();
    this.mockPlayer = player;
    this.mockProfile = profile;
    // Save initial state
    storePlayer(this.mockPlayer);
  }

  // Since this is a mock bridge, sendMessage is actually "receiving" messages from the client sdk, and should respond by calling onMessage directly
  sendMessage(message: HostMessage) {
    this.messageLog.push(message);
    console.log("[MockBridge] Received message:", message);
    // make it async, the send-receive should not be sync
    setTimeout(() => this.receiveMessage(message), 1);
  }

  takeScreenshot(): Promise<string> {
    const conversationId = createConversationId();
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingScreenshots.delete(conversationId);
        reject(new Error("Timed out waiting for game screenshot"));
      }, 10_000);

      this.pendingScreenshots.set(conversationId, (message) => {
        clearTimeout(timeout);
        this.pendingScreenshots.delete(conversationId);
        if (message.success) {
          resolve(message.image);
        } else {
          reject(new Error(`Game screenshot failed: ${message.error}`));
        }
      });

      try {
        this.onMessage({ type: "TakeScreenshot", conversationId });
      } catch (error) {
        clearTimeout(timeout);
        this.pendingScreenshots.delete(conversationId);
        reject(
          error instanceof Error
            ? error
            : new Error("Screenshot capture failed"),
        );
      }
    });
  }

  private receiveMessage(message: HostMessage) {
    // Handle each message type exhaustively
    switch (message.type) {
      case "BeginPlatformLogin":
        this.handleBeginLogin(message);
        break;
      case "BeginPlatformRegistrationOverlay":
        this.registrationOverlay.begin(message);
        break;
      case "PlatformRegistrationOverlayLogin":
        this.registrationOverlay.login(message);
        break;
      case "DismissPlatformRegistrationOverlay":
        this.registrationOverlay.dismiss(message);
        break;
      case "AcquireLoginLease":
        this.handleAcquireLoginLease(message);
        break;
      case "ScheduleNotificationV2":
        this.handleNotification(message);
        break;
      case "ScheduleInteractiveNotification":
        console.log("[MockBridge] ScheduleInteractiveNotification", message);
        break;
      case "UnscheduleNotification":
        this.handleUnschedule(message);
        break;
      case "UpdatePlayerData":
        this.handleUpdatePlayerData(message);
        break;
      case "RedirectToGame":
        showRedirectDialog(message);
        break;
      case "RedirectToTeamGame":
        // The mock cannot verify team ownership; accept and show the mock redirect.
        this.onMessage({
          type: "RedirectToTeamGameResponse",
          conversationId: message.conversationId,
          success: true,
        });
        showRedirectDialog({ ...message, type: "RedirectToGame" });
        break;
      case "RedirectToExplorePage":
        window.alert("You would be redirected to the explore page now");
        break;
      case "OpenLegalPage":
        showLegalPageDialog(message);
        break;
      case "Initialized":
        console.log(
          `[MockBridge] Initialized with sdkContextId: ${message.sdkContextId}, windowLocationHref: ${message.windowLocationHref}`,
        );
        this.onMessage({
          type: "SetPlayer",
          player: this.mockPlayer,
          profile: this.mockProfile,
          entrySource: { type: "direct" },
        });
        break;
      case "CaptureEvent":
        recordMockAnalyticsEventForMockBridge({
          event: `game_event:${message.event}`,
          properties: message.properties,
        });
        console.log(`[Analytics Event] ${message.event}`, message.properties);
        break;
      case "OnboardingCaptureEvent":
        recordMockAnalyticsEventForMockBridge({
          event: message.event,
          properties: message.properties,
        });
        console.log(`[Analytics Onboarding Event] ${message.event}`);
        break;
      case "SdkMethodCalled":
        console.log(
          `[SDK Method Call Event] ${message.method} (${message.args})`,
        );
        break;
      case "SdkMethodError":
        console.error(
          `[SDK Method Error] ${message.method} - ${message.error}`,
        );
        break;
      case "BeginPlatformPurchase":
        console.log(
          `[MockBridge] BeginPlatformPurchase with productId: ${message.productSku}`,
        );
        this.handleBeginPlatformPurchase(message);
        break;
      case "GetGameProducts":
        console.log("[MockBridge] GetGameProducts called");
        this.handleGetGameProducts(message);
        break;
      case "GetSubscriptions":
        console.log("[MockBridge] GetSubscriptions called");
        this.handleGetSubscriptions(message);
        break;
      case "CompletePlatformPurchase":
        console.log(
          `[MockBridge] CompletePlatformPurchase with token: ${message.purchaseToken}`,
        );
        this.handleCompletePlatformPurchase(message);
        break;
      case "GetIncompletePurchases":
        console.log(`[MockBridge] GetIncompletePurchases`);
        this.handleGetIncompletePurchases(message);
        break;
      case "ShowRewardedAd":
        // The mock has no ads to play, so let the developer pick each outcome.
        this.onMessage({
          type: "ShowRewardedAdResponse",
          conversationId: message.conversationId,
          success: true,
          result: window.confirm(
            "[MockBridge] Show a rewarded ad? Cancel answers that no ad is available.",
          )
            ? "started"
            : "no_fill",
        });
        break;
      case "GetIncompleteRewards":
        this.onMessage({
          type: "GetIncompleteRewardsResponse",
          conversationId: message.conversationId,
          success: true,
          rewards: [],
          rewardsSigned: "",
        });
        break;
      case "CompleteReward":
        this.onMessage({
          type: "CompleteRewardResponse",
          conversationId: message.conversationId,
          result: "error",
          error: "invalid_reward",
        });
        break;
      case "GetPlayerSigned":
        console.log(`[MockBridge] GetPlayerSigned`);
        this.handleGetPlayerSigned(message);
        break;
      case "ShareReferralLink":
        console.log(
          `[MockBridge] ShareReferralLink with reference: ${message.reference}`,
        );
        this.handleShareReferralLink(message);
        break;
      case "ShareImage":
        console.log(`[MockBridge] ShareImage`, {
          hasImage: message.image !== undefined,
          entryPayload: message.entryPayload,
        });
        this.onMessage({
          type: "ShareImageResponse",
          conversationId: message.conversationId,
          success: true,
          canceled: false,
        });
        break;
      case "GetReferrals":
        console.log(`[MockBridge] GetReferrals`);
        this.handleGetReferrals(message);
        break;
      case "ValidateName":
        console.log(`[MockBridge] ValidateName`);
        this.handleValidateName(message);
        break;
      case "IframeBfcacheRestore":
        // Diagnostic message for BFCache restoration - just log it
        console.log(`[MockBridge] IframeBfcacheRestore`, message);
        break;
      case "GetFeatureFlag":
        console.log(`[MockBridge] GetFeatureFlag: ${message.key}`);
        this.onMessage({
          type: "GetFeatureFlagResponse",
          conversationId: message.conversationId,
          success: true,
          value: undefined,
        });
        break;
      case "SetLoadingProgress":
        console.log(`[MockBridge] SetLoadingProgress: ${message.progress}%`);
        break;
      case "GameLoaded":
        console.log(`[MockBridge] GameLoaded`);
        break;
      case "FirstMilestone":
        console.log(`[MockBridge] FirstMilestone`);
        break;
      case "TakeScreenshotResponse":
        console.log(
          `[MockBridge] TakeScreenshotResponse: success=${message.success}`,
        );
        if (message.conversationId) {
          this.pendingScreenshots.get(message.conversationId)?.(message);
        }
        break;
      case "SendReservedLoginMessage":
        console.log(`[MockBridge] SendReservedLoginMessage: ${message.link}`);
        window.location.href = message.link;
        break;
      case "ReserveBrowserContinuation": {
        console.log("[MockBridge] ReserveBrowserContinuation");
        const link = resolveBrowserContinuationPath(
          message.path ?? "/g/mock-flagship-game",
          "https://jest.com",
        );
        if (!link) {
          this.onMessage({
            type: "BrowserContinuationReserved",
            success: false,
            error: "invalid_path",
            conversationId: message.conversationId,
          });
          break;
        }
        if (message.entryPayload) {
          link.searchParams.set("entryPayload", message.entryPayload);
        }
        this.onMessage({
          type: "BrowserContinuationReserved",
          success: true,
          token: link.toString(),
          conversationId: message.conversationId,
        });
        break;
      }
      case "OpenReservedBrowserContinuation":
        console.log(
          `[MockBridge] OpenReservedBrowserContinuation: ${message.token}`,
        );
        window.open(message.token, "_blank");
        break;
      case "BeginPlatformSubscription":
        console.log(
          `[MockBridge] BeginPlatformSubscription: ${message.subscriptionSku}`,
        );
        this.onMessage({
          type: "BeginPlatformSubscriptionResponse",
          conversationId: message.conversationId,
          result: "cancel",
        });
        break;
      case "CancelPlatformSubscription":
        console.log(
          `[MockBridge] CancelPlatformSubscription: ${message.subscriptionSku}`,
        );
        this.onMessage({
          type: "CancelPlatformSubscriptionResponse",
          conversationId: message.conversationId,
          result: "cancel",
        });
        break;
      case "ClaimRetentionOffer": {
        console.log(
          `[MockBridge] ClaimRetentionOffer: ${message.subscriptionSku}`,
        );
        if (!this.mockPlayer.registered) {
          this.onMessage({
            type: "ClaimRetentionOfferResponse",
            conversationId: message.conversationId,
            result: "error",
            error: "guest_not_allowed",
          });
          break;
        }
        const claimed = this.mockSubscriptions.find(
          (sub) =>
            sub.sku === message.subscriptionSku && sub.status === "active",
        );
        // Repeat claims re-confirm and succeed, mirroring the production claim contract.
        const isRepeatClaim =
          claimed !== undefined && this.claimedRetentionSkus.has(claimed.sku);
        if (!claimed || (!isRepeatClaim && claimed.retentionOffer === null)) {
          this.onMessage({
            type: "ClaimRetentionOfferResponse",
            conversationId: message.conversationId,
            result: "error",
            error: "not_eligible",
          });
          break;
        }
        this.claimedRetentionSkus.add(claimed.sku);
        claimed.retentionOffer = null;
        this.onMessage({
          type: "ClaimRetentionOfferResponse",
          conversationId: message.conversationId,
          result: "success",
          subscription: claimed,
          subscriptionSigned: this.mockSubscriptionsSigned,
        });
        break;
      }
      default:
        // Type checking to ensure we're handling all message types
        assertUnreachable(message);
    }
  }

  private handleBeginPlatformPurchase(
    message: Extract<HostMessage, { type: "BeginPlatformPurchase" }>,
  ) {
    console.log(
      `[MockBridge] Handling BeginPlatformPurchase with mock response: ${this.mockPurchaseResponse}`,
    );

    switch (this.mockPurchaseResponse) {
      case "success": {
        // Generate a random purchase token
        const purchaseToken =
          "mock_token_" + Math.random().toString(36).substring(2, 15);
        const product = this.mockProducts.find(
          (p) => p.sku === message.productSku,
        );
        const price = product?.price ?? DEFAULT_MOCK_PRODUCT_PRICE;
        this.onMessage({
          type: "BeginPlatformPurchaseResponse",
          conversationId: message.conversationId,
          result: "success",
          purchase: {
            purchaseToken,
            productSku: message.productSku,
            credits: price,
            createdAt: Date.now(),
            completedAt: null,
            estimatedRevenue: estimatePublisherRevenue(price),
            price,
            currency: product?.currency ?? "USD",
          },
          purchaseSigned: "JWS",
        });
        break;
      }
      case "cancel": {
        this.onMessage({
          type: "BeginPlatformPurchaseResponse",
          conversationId: message.conversationId,
          result: "cancel",
        });
        break;
      }
      case "error:internal_error": {
        this.onMessage({
          type: "BeginPlatformPurchaseResponse",
          conversationId: message.conversationId,
          result: "error",
          error: "internal_error",
        });
        break;
      }
      case "error:invalid_product": {
        this.onMessage({
          type: "BeginPlatformPurchaseResponse",
          conversationId: message.conversationId,
          result: "error",
          error: "invalid_product",
        });
        break;
      }
    }
  }

  private handleCompletePlatformPurchase(
    _message: Extract<HostMessage, { type: "CompletePlatformPurchase" }>,
  ) {
    console.log(
      `[MockBridge] Handling CompletePlatformPurchase with mock response: ${this.mockCompletePurchaseResponse}`,
    );

    switch (this.mockCompletePurchaseResponse) {
      case "success": {
        this.onMessage({
          type: "CompletePlatformPurchaseResponse",
          conversationId: _message.conversationId,
          result: "success",
        });
        break;
      }
      case "error:internal_error": {
        this.onMessage({
          type: "CompletePlatformPurchaseResponse",
          conversationId: _message.conversationId,
          result: "error",
          error: "internal_error",
        });
        break;
      }
      case "error:invalid_token": {
        this.onMessage({
          type: "CompletePlatformPurchaseResponse",
          conversationId: _message.conversationId,
          result: "error",
          error: "invalid_token",
        });
        break;
      }
    }
  }

  private handleGetGameProducts(
    _message: Extract<HostMessage, { type: "GetGameProducts" }>,
  ) {
    console.log("[MockBridge] Handling GetGameProducts");

    this.onMessage({
      type: "GetGameProductsResponse",
      conversationId: _message.conversationId,
      success: true,
      products: this.mockProducts,
    });
  }

  private handleGetSubscriptions(
    _message: Extract<HostMessage, { type: "GetSubscriptions" }>,
  ) {
    console.log("[MockBridge] Handling GetSubscriptions");

    this.onMessage({
      type: "GetSubscriptionsResponse",
      conversationId: _message.conversationId,
      success: true,
      subscriptions: this.mockSubscriptions,
      signed: this.mockSubscriptionsSigned,
    });
  }

  private handleGetIncompletePurchases(
    _message: Extract<HostMessage, { type: "GetIncompletePurchases" }>,
  ) {
    console.log("[MockBridge] Handling GetIncompletePurchases");

    if (this.mockIncompletePurchase) {
      const firstProduct = this.mockProducts[0];
      const sku = firstProduct ? firstProduct.sku : "default_product_sku";
      const price = firstProduct?.price ?? DEFAULT_MOCK_PRODUCT_PRICE;
      const purchaseToken =
        "incomplete_mock_token_" + Math.random().toString(36).substring(2, 15);

      this.onMessage({
        type: "GetIncompletePurchasesResponse",
        conversationId: _message.conversationId,
        success: true,
        hasMore: false,
        purchases: [
          {
            productSku: sku,
            purchaseToken: purchaseToken,
            credits: price,
            createdAt: Date.now(),
            completedAt: null,
            estimatedRevenue: estimatePublisherRevenue(price),
            price,
            currency: firstProduct?.currency ?? "USD",
          },
        ],
        purchasesSigned: "JWS",
      });
    } else {
      this.onMessage({
        type: "GetIncompletePurchasesResponse",
        conversationId: _message.conversationId,
        success: true,
        hasMore: false,
        purchases: [],
        purchasesSigned: "JWS",
      });
    }
  }

  private handleGetPlayerSigned(
    message: Extract<HostMessage, { type: "GetPlayerSigned" }>,
  ) {
    this.onMessage({
      type: "GetPlayerSignedResponse",
      conversationId: message.conversationId,
      success: true,
      player: {
        playerId: this.mockPlayer.playerId,
        registered: this.mockPlayer.registered,
        username: this.mockPlayer.username,
        avatarUrl: this.mockPlayer.avatarUrl,
      },
      playerSigned: "mock-signed-player-jwt-token",
    });
  }

  private handleBeginLogin(
    message: Extract<HostMessage, { type: "BeginPlatformLogin" }>,
  ) {
    console.log(
      `[MockBridge] BeginPlatformLogin with entryPayload: ${message.entryPayload}`,
    );

    showLoginPrompt((result) => {
      console.log(`[MockBridge] Login prompt resolved with: ${result}`);
      // Notify the SDK that the login popup has closed so sdk.login() can resolve.
      this.onMessage({
        type: "PlatformLoginClosed",
        conversationId: message.conversationId,
      });
      if (result === "login") {
        this.setMockPlayer({ registered: true });
      }
    });
  }

  private handleAcquireLoginLease(
    message: Extract<HostMessage, { type: "AcquireLoginLease" }>,
  ) {
    console.log(
      `[MockBridge] AcquireLoginLease with message: ${message.message}`,
    );

    if (this.rejectLoginLeaseAcquisition) {
      console.log("[MockBridge] Rejecting login lease acquisition");
      this.onMessage({
        type: "LoginLeaseAcquired",
        success: false,
        error: "internal_error",
        link: "",
      });
      return;
    }

    let link: string;

    // If Skip login SMS is enabled, skip SMS preview and set mock_login directly
    if (this.skipLoginSms) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("mock_login", "true");
      console.log("[MockBridge] Skipping SMS and setting mock_login directly");
      link = currentUrl.toString();
    } else {
      // Default SMS flow when Skip login SMS is OFF
      // Do we support sms links?
      if (doesBrowserSupportSMSLinks({ userAgent: navigator.userAgent })) {
        link = smsLink({
          phoneNumber: "5555555555",
          message: message.message,
          rbmAgentId: "agent-123",
          smsMessagePrefix: "PREFIX:",
          userAgent: navigator.userAgent,
        });
      } else {
        // Generate a link based on current URL with a mock_login parameter
        const escapedMessage = message.message
          .replace(/"/g, '\\"')
          .replace(/'/g, "\\'");

        link = `javascript:alert('Sending SMS with text: ${escapedMessage}')`;
      }
    }

    this.onMessage({
      type: "LoginLeaseAcquired",
      success: true,
      link: link,
      conversationId: message.conversationId,
    });
  }

  private handleUpdatePlayerData(
    message: Extract<HostMessage, { type: "UpdatePlayerData" }>,
  ) {
    console.log(`[MockBridge] Updating player state with ID: ${message.id}`);
    this.updatePlayerData(message.update);
    // Send acknowledgment immediately
    this.onMessage({
      type: "AckUpdatePlayerData",
      id: message.id,
      conversationId: message.conversationId,
    });

    // Notify UI to refresh
    this.onStateChange?.();
  }

  private handleNotification(
    message: Extract<HostMessage, { type: "ScheduleNotificationV2" }>,
  ) {
    const notifications = this.getStoredNotifications();

    if (
      "scheduledInDays" in message &&
      typeof message.scheduledInDays === "number" &&
      message.scheduledInDays > MAX_NOTIFICATION_SCHEDULE_DAYS
    ) {
      throw new Error(
        `scheduledInDays must be at most ${MAX_NOTIFICATION_SCHEDULE_DAYS}`,
      );
    }

    if ("scheduledAt" in message && message.scheduledAt) {
      const maxDate = new Date(
        Date.now() + MAX_NOTIFICATION_SCHEDULE_DAYS * 24 * 60 * 60 * 1000,
      );
      if (new Date(message.scheduledAt) > maxDate) {
        throw new Error(
          `scheduledAt must be within ${MAX_NOTIFICATION_SCHEDULE_DAYS} days`,
        );
      }
    }

    const scheduledAt =
      "scheduledInDays" in message &&
      typeof message.scheduledInDays === "number"
        ? new Date(
            Math.max(
              Date.now() + MIN_NOTIFICATION_SCHEDULE_MS,
              Date.now() + message.scheduledInDays * 24 * 60 * 60 * 1000,
            ),
          ).toISOString()
        : message.scheduledAt;

    if (!scheduledAt) {
      throw new Error("Either scheduledAt or scheduledInDays must be provided");
    }

    const newNotification: MockNotification = {
      body: message.body,
      ctaText: message.ctaText,
      ...(message.assetReference
        ? { assetReference: message.assetReference }
        : {}),
      ...(message.asset ? { hasInlineAsset: true as const } : {}),
      scheduledAt,
      ...("scheduledInDays" in message &&
      typeof message.scheduledInDays === "number"
        ? { scheduledInDays: message.scheduledInDays }
        : {}),
      identifier: message.identifier,
      createdAt: new Date().toISOString(),
    };

    if (message.identifier) {
      const index = notifications.findIndex(
        (n) => n.identifier === message.identifier,
      );
      if (index !== -1) {
        notifications[index] = newNotification;
      } else {
        notifications.push(newNotification);
      }
    } else {
      notifications.push(newNotification);
    }

    localStorage.setItem(MOCK_NOTIFICATIONS_KEY, JSON.stringify(notifications));
    this.onNotificationChange?.();
  }

  private handleUnschedule(
    message: Extract<HostMessage, { type: "UnscheduleNotification" }>,
  ) {
    const notifications = this.getStoredNotifications().filter(
      (n) => n.identifier !== message.identifier,
    );
    localStorage.setItem(MOCK_NOTIFICATIONS_KEY, JSON.stringify(notifications));
    this.onNotificationChange?.();
  }

  getStoredNotifications(): MockNotification[] {
    const stored = localStorage.getItem(MOCK_NOTIFICATIONS_KEY);
    return stored ? (JSON.parse(stored) as MockNotification[]) : [];
  }

  clearNotifications() {
    localStorage.setItem(MOCK_NOTIFICATIONS_KEY, JSON.stringify([]));
    this.onNotificationChange?.();
  }

  notifyPlatformExitRequested() {
    console.log("[MockBridge] Sending PlatformExitRequested");
    this.onMessage({ type: "PlatformExitRequested" });
  }

  // Debug API for game developers
  resetPlayer() {
    localStorage.removeItem(MOCK_PLAYER_STORAGE_KEY);
    localStorage.removeItem(MOCK_NOTIFICATIONS_KEY);
    this.messageLog = [];

    // Create new guest player
    this.mockPlayer = {
      playerId: generatePlayerId(),
      registered: false,
      username: null,
      avatarUrl: null,
      entryPayload: {},
      playerData: {},
    };

    // Save the guest state
    storePlayer(this.mockPlayer);

    this.onMessage({
      type: "SetPlayer",
      player: { ...this.mockPlayer },
      profile: { ...this.mockProfile },
      entrySource: { type: "direct" },
    });

    setTimeout(() => window.location.reload(), 50);
  }

  setMockPlayer(player: Partial<GamePlayer>) {
    // Allow manual ID changes now
    this.mockPlayer = { ...this.mockPlayer, ...player };
    storePlayer(this.mockPlayer);
    this.onStateChange?.();

    // Reload if critical properties changed
    if (
      "playerId" in player ||
      "registered" in player ||
      "entryPayload" in player
    ) {
      setTimeout(() => window.location.reload(), 50);
    }
  }

  setOnNotificationChange(callback: () => void) {
    this.onNotificationChange = callback;
  }

  setOnStateChange(callback: () => void) {
    this.onStateChange = callback;
  }

  getMockPlayer(): GamePlayer {
    return { ...this.mockPlayer };
  }

  getMessageLog(): HostMessage[] {
    return [...this.messageLog];
  }

  clearMessageLog() {
    this.messageLog = [];
  }

  updatePlayerData(update: PlayerDataUpdate) {
    this.setMockPlayer({
      playerData: { ...this.mockPlayer.playerData, ...update.update },
    });
  }

  getRejectLoginLeaseAcquisition() {
    return this.rejectLoginLeaseAcquisition;
  }

  setRejectLoginLeaseAcquisition(value: boolean) {
    this.rejectLoginLeaseAcquisition = value;
  }

  getSkipLoginSms() {
    return this.skipLoginSms;
  }

  setSkipLoginSms(value: boolean) {
    this.skipLoginSms = value;
  }

  getMockPurchaseResponse() {
    return this.mockPurchaseResponse;
  }

  setMockPurchaseResponse(
    value:
      | "success"
      | "cancel"
      | "error:internal_error"
      | "error:invalid_product",
  ) {
    this.mockPurchaseResponse = value;
  }

  getMockCompletePurchaseResponse() {
    return this.mockCompletePurchaseResponse;
  }

  setMockCompletePurchaseResponse(
    value: "success" | "error:internal_error" | "error:invalid_token",
  ) {
    this.mockCompletePurchaseResponse = value;
  }

  getMockProducts() {
    return [...this.mockProducts];
  }

  setMockProducts(products: ReturnType<MockBridge["getMockProducts"]>) {
    this.mockProducts = products;
  }

  getMockSubscriptions() {
    return [...this.mockSubscriptions];
  }

  setMockSubscriptions(
    subscriptions: ReturnType<MockBridge["getMockSubscriptions"]>,
  ) {
    this.mockSubscriptions = subscriptions;
  }

  getMockSubscriptionsSigned() {
    return this.mockSubscriptionsSigned;
  }

  setMockSubscriptionsSigned(signed: string) {
    this.mockSubscriptionsSigned = signed;
  }

  getMockIncompletePurchase() {
    return this.mockIncompletePurchase;
  }

  setMockIncompletePurchase(value: boolean) {
    this.mockIncompletePurchase = value;
  }

  private handleShareReferralLink(
    message: Extract<HostMessage, { type: "ShareReferralLink" }>,
  ) {
    console.log("[MockBridge] Handling ShareReferralLink", {
      reference: message.reference,
      shareImageBytes: message.shareImage?.length ?? 0,
    });

    this.onMessage({
      type: "ShareReferralLinkResponse",
      conversationId: message.conversationId,
      success: true,
      canceled: false,
    });
  }

  private handleGetReferrals(
    message: Extract<HostMessage, { type: "GetReferrals" }>,
  ) {
    console.log("[MockBridge] Handling GetReferrals");

    this.onMessage({
      type: "GetReferralsResponse",
      conversationId: message.conversationId,
      success: true,
      referrals: {
        "mock-reference": [],
      } as { [reference: string]: { playerId: string; joinedAt: string }[] },
      referralsSigned: "mock-signed-jwt-token",
    });
  }

  private handleValidateName(
    message: Extract<HostMessage, { type: "ValidateName" }>,
  ) {
    console.log("[MockBridge] Handling ValidateName");

    this.onMessage({
      type: "ValidateNameResponse",
      conversationId: message.conversationId,
      success: true,
      status: "valid",
    });
  }
}
